import { fetchWithHandledResponse, generatePostHeaders, handleResponse } from '../helpers';

import { apiUrl } from 'config';

export const fetchMonitoredDocumentChanges = (pageNumber = 1, pageSize = 10) => {
    return fetchWithHandledResponse(
        `${apiUrl}/document-monitor/changes?pageNumber=${pageNumber}&pageSize=${pageSize}`
    );
};

export const fetchMonitoredDocument = documentId => {
    return fetchWithHandledResponse(`${apiUrl}/document-monitor/${documentId}`, null, response => {
        if (response.status === 404) {
            // return empty response
            return null;
        } else {
            return handleResponse(response);
        }
    });
};

export const fetchMonitoredDocumentVersions = (documentId, pageNumber = 1, pageSize = 5) => {
    return fetchWithHandledResponse(
        `${apiUrl}/document-monitor/${documentId}/versions?pageNumber=${pageNumber}&pageSize=${pageSize}`,
        null,
        response => {
            if (response.status === 404) {
                // return empty response
                return {
                    data: [],
                    paging: {
                        total: 0,
                        pageNumber: 0,
                        pageSize: 0,
                        pages: 0,
                    },
                };
            } else {
                return handleResponse(response);
            }
        }
    );
};

export const fetchMonitoredDocumentVersion = (documentId, versionId) => {
    return fetchWithHandledResponse(
        `${apiUrl}/document-monitor/${documentId}/version/${versionId}`
    );
};

export const setMonitoredDocumentActiveStatus = (documentId, activeStatus) => {
    return fetchWithHandledResponse(
        `${apiUrl}/document-monitor/${documentId}/active/${activeStatus}`,
        {
            method: 'post',
            headers: generatePostHeaders(document),
            body: JSON.stringify(document),
        }
    );
};

export const fetchMonitoredDocumentMarkdown = (documentId, versionId) => {
    const url = versionId
        ? `${apiUrl}/document-monitor/${documentId}/version/${versionId}/markdown`
        : `${apiUrl}/document-monitor/${documentId}/markdown`;

    // authenticated request
    return fetchWithHandledResponse(url, null, response => {
        if (response.ok) {
            // handle json and markdown retrieval via S3 signed URL
            return response.json().then(jsonResponse => {
                return fetch(jsonResponse.url).then(markdownResponse => {
                    if (markdownResponse.ok) {
                        return markdownResponse.text().then(data => {
                            return data;
                        });
                    } else {
                        const error =
                            (markdownResponse && markdownResponse.message) ||
                            markdownResponse.statusText;
                        throw new Error(error);
                    }
                });
            });
        } else {
            throw new Error(response.statusText);
        }
    });
};

export const setMonitoredDocumentValidated = (documentId, s3Version, significant, comment) => {
    let requestUrl = `${apiUrl}/document-monitor/${documentId}/version/${s3Version}/validated?significant=${
        significant || false
    }`;

    if (comment) {
        requestUrl += `&comment=${comment}`;
    }

    return fetchWithHandledResponse(requestUrl, {
        method: 'post',
        headers: generatePostHeaders(null),
        body: null,
    });
};

export const fetchMonitoredDocumentCompare = (documentId, oldVersionId, newVersionId) => {
    return fetchWithHandledResponse(
        `${apiUrl}/document-monitor/${documentId}/compare-versions/${oldVersionId},${newVersionId}`
    ).then(data => {
        const documentPromise = new Promise(resolve => {
            resolve(data);
        });
        const oldMarkdownPromise = retrieveTextContentFromSignedUrl(
            data.oldVersion.markdownSignedUrl
        );
        const newMarkdownPromise = retrieveTextContentFromSignedUrl(
            data.newVersion.markdownSignedUrl
        );

        return Promise.all([documentPromise, oldMarkdownPromise, newMarkdownPromise]).then(
            result => {
                result[0].oldVersion.markdown = result[1];
                result[0].newVersion.markdown = result[2];
                return result[0];
            }
        );
    });
};

export const retrieveTextContentFromSignedUrl = signedUrl => {
    return fetch(signedUrl).then(response => {
        if (response.ok) {
            return response.text().then(data => {
                return data;
            });
        } else {
            const error = (response && response.message) || response.statusText;
            throw new Error(error);
        }
    });
};
